import { getDataSourceSrv } from '@grafana/runtime';
import { NodeData, type NodeDataParams, StructuredNodeData, type StructuredNodeDataParams } from './base';
import { ComponentNodeData, type CreateComponentContextParams } from './component';
import {
  DashboardNodeData,
  FolderNodeData,
  type CreateDashboardContextParams,
  type CreateFolderContextParams,
} from './dashboard';
import { DatasourceNodeData, type CreateDatasourceContextParams } from './datasource';
import { EntityNodeData, type CreateEntityContextParams } from './entity';
import {
  LabelNameNodeData,
  LabelValueNodeData,
  type CreateLabelNameContextParams,
  type CreateLabelValueContextParams,
} from './label';

/**
 * registry to handle the type mappings between ItemData type, params and node data
 */
export type ContextTypeRegistry = {
  datasource: { params: CreateDatasourceContextParams; node: DatasourceNodeData };
  label_name: { params: CreateLabelNameContextParams; node: LabelNameNodeData };
  label_value: { params: CreateLabelValueContextParams; node: LabelValueNodeData };
  dashboard: { params: CreateDashboardContextParams; node: DashboardNodeData };
  dashboard_folder: { params: CreateFolderContextParams; node: FolderNodeData };
  structured: { params: StructuredNodeDataParams; node: StructuredNodeData };
  component: { params: CreateComponentContextParams; node: ComponentNodeData };
  entity: { params: CreateEntityContextParams; node: EntityNodeData };
  unknown: { params: NodeDataParams; node: NodeData };
};

export type DatasourceMeta = {
  datasourceType: string;
  datasourceName: string;
  img?: string;
};

/**
 * Helper function to get datasource type from datasource UID
 */
function getDatasourceMeta(datasourceUid: string): DatasourceMeta {
  const datasourceInstance = getDataSourceSrv().getInstanceSettings(datasourceUid);
  return {
    datasourceType: datasourceInstance?.type ?? 'unknown',
    datasourceName: datasourceInstance?.name ?? datasourceUid,
    img: datasourceInstance?.meta?.info?.logos?.small,
  };
}

/**
 * Creates context nodes based on type with full type safety using generics
 */
export function createContextByType<T extends keyof ContextTypeRegistry>(
  type: T,
  params: ContextTypeRegistry[T]['params']
): ContextTypeRegistry[T]['node'] {
  switch (type) {
    case 'datasource':
      return new DatasourceNodeData({
        ...(params as CreateDatasourceContextParams),
        ...getDatasourceMeta((params as CreateDatasourceContextParams).datasourceUid),
      });
    case 'label_name':
      return new LabelNameNodeData({
        ...(params as CreateLabelNameContextParams),
        ...getDatasourceMeta((params as CreateLabelNameContextParams).datasourceUid),
      });
    case 'label_value':
      return new LabelValueNodeData({
        ...(params as CreateLabelValueContextParams),
        ...getDatasourceMeta((params as CreateLabelValueContextParams).datasourceUid),
      });
    case 'dashboard':
      return new DashboardNodeData(params as CreateDashboardContextParams);
    case 'dashboard_folder':
      return new FolderNodeData(params as CreateFolderContextParams);
    case 'structured':
      return new StructuredNodeData(params as StructuredNodeDataParams);
    case 'component':
      return new ComponentNodeData({
        ...(params as CreateComponentContextParams),
        hidden: true,
      });
    case 'entity':
      return new EntityNodeData(params as CreateEntityContextParams);
    case 'unknown':
      return new NodeData(params as NodeDataParams);
    default:
      console.error(`Unknown context type: ${type}`);
      return new NodeData(params as NodeDataParams);
  }
}
